/**
 * TieToMatterStep - wizard step for optional matter binding.
 *
 * Visual treatment mirrors ChooseTemplateStep - card grid with description,
 * search input, magnifying-glass adornment, sticky scroll. The first card
 * is the explicit "Standalone (no matter)" choice, which doubles as the
 * default. Selecting a matter reports back uuid + jurisdiction so Step 4
 * (matter context toggles) can gate availability against jurisdiction +
 * matter data.
 *
 * Storage / linking behaviour (for context):
 *   - On wizard submit the backend sets `matter_drafts.matter_uuid` AND
 *     inserts a polymorphic row in `draft_attachments` keyed by entity_id.
 *   - DraftingDetail uses that linkage to render the Matters > <title> >
 *     <draft> breadcrumb with clickable links to /matters/<uuid>.
 *   - `_audit('draft_create', …)` on the backend logs the linkage to the
 *     audit table for compliance.
 */

import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { PiBriefcaseLight, PiCheckLight, PiXLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import SearchInput from '../../shared/TailwindComponents/SearchInput';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { FOCUS_RING } from '../../shared/TailwindComponents/FieldShell';
import { SELECTED_CHECK_DISC, selectedCardClass } from '../../shared/brand/selectedCard';
import { useAppDispatch } from '../../../store';
import { fetchMatters } from '../../../store/actions/Matters/MatterActions';

export interface MatterOption {
  uuid: string;
  title: string;
  jurisdiction?: string | null;
  description?: string | null;
  status?: string | null;
  stage?: string | null;
  /** Human-readable client display name; surfaced under the title. */
  clientName?: string | null;
  /** Updated-at ISO string for the "Updated x days ago" line. */
  updatedAt?: string | null;
  /** Created-at ISO string - drives newest-first ordering. */
  createdAt?: string | null;
}

/** Parse an ISO date to epoch ms, or 0 when missing/invalid (sorts last). */
const toTime = (iso?: string | null): number => {
  const ms = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

interface TieToMatterStepProps {
  value: string;
  onChange: (matterUuid: string, matter: MatterOption | null) => void;
}

const relativeTime = (iso: string | null | undefined, t: any): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return t('Updated today');
  if (days === 1) return t('Updated yesterday');
  if (days < 30) return t('Updated {{n}} days ago', { n: days });
  const months = Math.floor(days / 30);
  if (months < 12) return t('Updated {{n}} months ago', { n: months });
  return t('Updated over a year ago');
};

const TieToMatterStep: React.FC<TieToMatterStepProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [matters, setMatters] = useState<MatterOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const result: any = await dispatch(fetchMatters({ page: 1, page_size: 100 }) as any);
        if (!alive) return;
        const list: MatterOption[] = (result?.matters ?? [])
          .filter((m: any) => m.uuid)
          .map((m: any) => ({
            uuid: m.uuid,
            title: m.title || m.name || t('Untitled matter'),
            jurisdiction: m.jurisdiction ?? null,
            description: m.description ?? null,
            status: m.status_name || m.status || null,
            stage: m.stage_name || m.stage || null,
            clientName: m.client?.name || m.client?.Name || null,
            updatedAt: m.updated_at || m.updatedAt || null,
            createdAt: m.created_at || m.createdAt || null,
          }));
        // Newest-created first, consistently - the API order isn't guaranteed.
        list.sort((a, b) => toTime(b.createdAt ?? b.updatedAt) - toTime(a.createdAt ?? a.updatedAt));
        setMatters(list);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dispatch, t]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return matters;
    return matters.filter((m) =>
      m.title.toLowerCase().includes(q)
      || (m.description ?? '').toLowerCase().includes(q)
      || (m.clientName ?? '').toLowerCase().includes(q)
      || (m.jurisdiction ?? '').toLowerCase().includes(q),
    );
  }, [matters, search]);

  const standaloneSelected = value === '';

  return (
    <div className="space-y-5">
      {/* Standalone option - visually distinct dashed-border card so users
          see at a glance that it's an opt-out rather than a real matter. */}
      <button
        type="button"
        onClick={() => onChange('', null)}
        className={clsx(
          'w-full rounded-xl border p-4 text-start transition-all',
          FOCUS_RING,
          selectedCardClass(
            standaloneSelected,
            'border-dashed border-hairline bg-surface-sunken/70 hover:bg-surface-sunken',
          ),
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <PiXLight className="h-4 w-4 text-fg-muted" />
              <span className="text-sm font-medium text-fg-strong">
                {t('Standalone (no matter)')}
              </span>
            </div>
            <p className="mt-1 text-xs text-fg-muted">
              {t('Generate without pulling in matter-specific context. You can attach a matter later from the draft\'s detail view.')}
            </p>
          </div>
          {standaloneSelected && (
            <span className={SELECTED_CHECK_DISC}>
              <PiCheckLight size={11} className="text-ink" />
            </span>
          )}
        </div>
      </button>

      {/* Search */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('Search matters by name, client, or jurisdiction…')}
      />

      {/* Card grid - no nested scroll: BaseForm owns the form scroll, so
          this just renders the grid inline. Mirrors ChooseTemplateStep. */}
      {loading ? (
        <CardGridSkeleton />
      ) : filtered.length === 0 ? (
        <p className="text-sm italic text-fg-muted">
          {search ? t('No matters match your search.') : t('No matters available.')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {filtered.map((m) => (
            <MatterCard
              key={m.uuid}
              matter={m}
              selected={m.uuid === value}
              onSelect={() => onChange(m.uuid, m)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── MatterCard ───────────────────────────────────────────────────────────────

const MatterCard: React.FC<{ matter: MatterOption; selected: boolean; onSelect: () => void }> = ({
  matter, selected, onSelect,
}) => {
  const { t } = useTranslation();
  const updatedRel = relativeTime(matter.updatedAt, t);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        'group relative rounded-xl border p-4 text-start transition-all',
        FOCUS_RING,
        selectedCardClass(selected),
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <PiBriefcaseLight className="h-4 w-4 text-fg-muted" />
            <span className="truncate text-sm font-medium text-fg-strong">
              {matter.title}
            </span>
          </div>
          {matter.clientName && (
            <p className="mt-1 text-[11px] text-fg-muted truncate">
              {t('Client')}: {matter.clientName}
            </p>
          )}
          {matter.description && (
            <p className="mt-1 line-clamp-2 text-xs text-fg-muted">
              {matter.description}
            </p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {matter.stage && <MetaChip>{matter.stage}</MetaChip>}
            {matter.status && <MetaChip>{matter.status}</MetaChip>}
            {matter.jurisdiction && <MetaChip>{matter.jurisdiction}</MetaChip>}
            {updatedRel && (
              <span className="text-[10px] text-fg-muted">{updatedRel}</span>
            )}
          </div>
        </div>
        {selected && (
          <span className={SELECTED_CHECK_DISC}>
            <PiCheckLight size={11} className="text-ink" />
          </span>
        )}
      </div>
    </button>
  );
};

/** Quiet capitalized metadata pill (stage / status / jurisdiction). */
const MetaChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-[10px] capitalize text-fg-body">
    {children}
  </span>
);

/** Four card-shaped placeholders in the same 2-up grid the results use. */
const CardGridSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
    {[1, 2, 3, 4].map((n) => (
      <Skeleton key={n} variant="card" />
    ))}
  </div>
);

export default TieToMatterStep;
